import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUS = {
  sent: ["Respondida en Google", "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"],
  sandbox: ["Respondida (simulación)", "bg-amber-500/10 text-amber-700 border-amber-500/30"],
  failed: ["Falló", "bg-rose-500/10 text-rose-700 border-rose-500/30"],
  not_applicable: ["No aplica", "bg-slate-500/10 text-slate-600 border-slate-500/30"],
};

function ReviewRow({ r }) {
  const [label, cls] = STATUS[r.auto_reply_status] || (r.reply ? ["Respondida manualmente", STATUS.not_applicable[1]] : ["Pendiente", STATUS.not_applicable[1]]);
  return (
    <div className="px-3 py-2 text-xs space-y-1" data-testid={`gbp-review-${r.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-amber-500 tracking-tight">{"★".repeat(r.stars || 0)}<span className="text-muted-foreground/40">{"★".repeat(5 - (r.stars || 0))}</span></span>
        <span className="font-medium">{r.author_name || "—"}</span>
        <span className="text-muted-foreground">· {r.location_title || "—"}</span>
        {r.simulated && <Badge variant="outline" className="text-[9px]">SIMULADA</Badge>}
        <span className={`ml-auto rounded-full border px-2 py-0.5 text-[10px] ${cls}`} data-testid={`gbp-review-status-${r.id}`}>{label}</span>
      </div>
      <div className="text-muted-foreground">{r.comment || <i>Sin comentario</i>}</div>
      {r.auto_reply_reason && <div className="text-[10px] text-muted-foreground">Motivo: {r.auto_reply_reason}</div>}
      {r.auto_reply_error && <div className="text-[10px] text-rose-600">{r.auto_reply_error}</div>}
    </div>
  );
}

export const GbpAutoReply = ({ cfg, patch, onSave }) => {
  const [reviews, setReviews] = useState([]);
  const [stars, setStars] = useState(2);
  const [comment, setComment] = useState("Pésimo servicio, me entregaron el vehículo tarde y sin lavar.");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const loadReviews = async () => {
    try { const { data } = await api.get("/gbp/reviews?limit=10"); setReviews(data.items || []); } catch { setReviews([]); }
  };
  useEffect(() => { loadReviews(); }, []);

  const simulate = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/gbp/reviews/simulate", { stars, comment, author: "Cliente de prueba", location: "SDM Kennedy" });
      setResult(data); loadReviews();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="p-5 border-b border-border space-y-4" data-testid="gbp-auto-reply">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">Respuesta automática a reseñas de baja calificación</div>
          <div className="text-xs text-muted-foreground">Se publica automáticamente una sola vez en reseñas de 1★ a 3★ (con o sin comentario). Revisión cada 15 minutos.</div>
        </div>
        <Switch checked={!!cfg.auto_reply_enabled} onCheckedChange={(v) => patch("auto_reply_enabled", v)} data-testid="gbp-auto-enabled" />
      </div>
      <div>
        <div>
          <Label>Texto de la respuesta</Label>
          <textarea value={cfg.auto_reply_text || ""} onChange={(e) => patch("auto_reply_text", e.target.value)} rows={3} maxLength={4000}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="gbp-auto-text" />
        </div>
      </div>
      <Button size="sm" onClick={onSave} data-testid="gbp-auto-save">Guardar respuesta automática</Button>

      <div className="border-t border-border pt-4 space-y-3" data-testid="gbp-positive-reply">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">Agradecimiento a reseñas positivas</div>
            <div className="text-xs text-muted-foreground">Se publica automáticamente una sola vez en reseñas de 4★ y 5★ (con o sin comentario).</div>
          </div>
          <Switch checked={cfg.positive_reply_enabled !== false} onCheckedChange={(v) => patch("positive_reply_enabled", v)} data-testid="gbp-positive-enabled" />
        </div>
        <div>
          <Label>Texto del agradecimiento</Label>
          <textarea value={cfg.positive_reply_text || ""} onChange={(e) => patch("positive_reply_text", e.target.value)} rows={2} maxLength={4000}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="gbp-positive-text" />
        </div>
        <Button size="sm" onClick={onSave} data-testid="gbp-positive-save">Guardar agradecimiento</Button>
      </div>

      <div className="border border-sky-500/30 bg-sky-500/5 rounded-md p-3 space-y-2">
        <div className="text-xs font-semibold text-sky-700">Simulador de reseña</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <select value={stars} onChange={(e) => setStars(Number(e.target.value))} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="gbp-sim-stars">
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{`${n}★`}</option>)}
          </select>
          <Input value={comment} onChange={(e) => setComment(e.target.value)} className="md:col-span-4 h-9 text-sm" placeholder="Comentario del cliente" data-testid="gbp-sim-comment" />
          <Button size="sm" variant="outline" className="h-9" disabled={busy} onClick={simulate} data-testid="gbp-sim-send">{busy ? "Evaluando…" : "Simular"}</Button>
        </div>
        {result && (
          <div className="text-xs bg-background/70 border border-border rounded p-2" data-testid="gbp-sim-result">
            {result.action === "replied" ? (
              <><b className="text-emerald-700">{result.kind === "positive" ? "Agradecimiento publicado" : "Disculpa publicada"}{result.sandbox ? " (simulación)" : ""}:</b> {result.reply}</>
            ) : (
              <><b>No se respondió:</b> {result.reason}</>
            )}
          </div>
        )}
      </div>

      <div className="border border-border rounded-md">
        <div className="px-3 py-2 text-xs font-semibold border-b border-border">Últimas reseñas</div>
        <div className="divide-y divide-border max-h-80 overflow-y-auto" data-testid="gbp-reviews-list">
          {reviews.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground">Sin reseñas todavía.</div>}
          {reviews.map((r) => <ReviewRow key={r.id} r={r} />)}
        </div>
      </div>
    </div>
  );
};

export default GbpAutoReply;
